import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { ensurePushSubscription, disablePushSubscription } from '../lib/webPush';
import { getServiceWorkerRegistration } from '../pwa/register-sw';

// AI-WF262 (要望#211 / §18 TA-5): push 購読の発火導線（🔔 通知トグル）。
// #205 の購読部品(webPush.ts)と #206 の SW(sw.js) は完成済みだが、購読を発火する UI が
// どこにも無かった（デッドコード）ため、ここで結線する。
//
// 状態機械:
//   unsupported  … SW/Push/Notification API が無い（insecure origin 等）→ 非表示
//   no-server-key… BE の VAPID 公開鍵未設定 → 無効表示（サーバ設定待ち）
//   denied       … ブラウザで通知がブロック済み → 無効表示（ブラウザ設定から解除）
//   off / on     … 購読 OFF / ON（クリックでトグル）
//   busy         … 購読/解除の処理中
// permission=granted かつ以前 ON にした痕跡（localStorage）があれば、マウント時に静かに
// ensurePushSubscription を呼び直す（VAPID 鍵ローテ・購読失効の自己修復。webPush.ts が張り直し対応済み）。
type PushState = 'unsupported' | 'no-server-key' | 'denied' | 'off' | 'on' | 'busy';

const LS_KEY = 'console.pushEnabled';

export function PushToggle() {
  const [state, setState] = useState<PushState>('busy');

  useEffect(() => {
    let alive = true;
    void (async () => {
      if (
        !('serviceWorker' in navigator) ||
        !('PushManager' in window) ||
        !('Notification' in window)
      ) {
        if (alive) setState('unsupported');
        return;
      }
      // BE の鍵が無いと購読自体が成立しない（enabled=false は送信側のみ無効なので購読は可）。
      let publicKey: string | null = null;
      try {
        const { data } = await apiClient.get<{ publicKey: string | null; enabled: boolean }>(
          '/api/admin/push/vapid-public-key',
        );
        publicKey = data.publicKey;
      } catch {
        publicKey = null;
      }
      if (!publicKey) {
        if (alive) setState('no-server-key');
        return;
      }
      if (Notification.permission === 'denied') {
        if (alive) setState('denied');
        return;
      }
      const reg = await getServiceWorkerRegistration();
      if (!reg) {
        if (alive) setState('unsupported');
        return;
      }
      const wanted = localStorage.getItem(LS_KEY) === '1';
      if (wanted && Notification.permission === 'granted') {
        // 自己修復（鍵ローテ・失効の張り直し）。失敗しても UI は現状表示に落とすだけ。
        try {
          const sub = await ensurePushSubscription(reg);
          if (alive) setState(sub ? 'on' : 'off');
          return;
        } catch {
          /* 下の実購読状態にフォールバック */
        }
      }
      const sub = await reg.pushManager.getSubscription();
      if (alive) setState(sub ? 'on' : 'off');
    })();
    return () => {
      alive = false;
    };
  }, []);

  const toggle = async () => {
    if (state !== 'on' && state !== 'off') return;
    const prev = state;
    setState('busy');
    try {
      const reg = await getServiceWorkerRegistration();
      if (!reg) {
        setState('unsupported');
        return;
      }
      if (prev === 'off') {
        const sub = await ensurePushSubscription(reg);
        if (sub) {
          localStorage.setItem(LS_KEY, '1');
          setState('on');
        } else {
          // 許可ダイアログで拒否された等。denied は以後無効表示。
          setState(Notification.permission === 'denied' ? 'denied' : 'off');
        }
      } else {
        await disablePushSubscription(reg);
        localStorage.setItem(LS_KEY, '0');
        setState('off');
      }
    } catch {
      setState(prev);
    }
  };

  if (state === 'unsupported') return null;

  const disabled = state === 'no-server-key' || state === 'denied' || state === 'busy';
  const on = state === 'on';
  const title =
    state === 'no-server-key'
      ? '通知はサーバ未設定（VAPID鍵）のため使えません'
      : state === 'denied'
        ? '通知がブラウザでブロックされています（ブラウザの設定から許可してください）'
        : state === 'busy'
          ? '処理中…'
          : on
            ? '完了通知ON（クリックでOFF）。非同期で送った用件の完了を通知します'
            : '完了通知OFF（クリックでON）。非同期で送った用件の完了を通知します';

  return (
    <button
      type="button"
      data-testid="push-toggle"
      data-push-state={state}
      onClick={() => void toggle()}
      disabled={disabled}
      title={title}
      style={{
        border: '1px solid #d1d5db',
        borderRadius: 8,
        background: on ? '#eef2ff' : '#fff',
        color: on ? '#3730a3' : '#616061',
        padding: '4px 10px',
        fontSize: 13,
        fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {on ? '🔔 通知ON' : '🔕 通知OFF'}
    </button>
  );
}
